import * as THREE from 'three';

/**
 * The one soft shadow (ADR-032): a directional light from the sun, its
 * orthographic camera fitted to the stage's cylinder as the light sees it,
 * so the 1024² shadow map is spent on where a lesson can be and nothing else.
 * Lesson objects cast and receive, the stage ground receives, scenery neither.
 */

/**
 * What the shadow map covers: a cylinder `radius` round `centre` ([x, z], the origin unless said),
 * from the floor to `height`. The stage's by default; a world may fit its own (the villa's house and
 * terrace, ADR-037).
 */
export type ShadowArea = {
  readonly radius: number;
  readonly height: number;
  readonly centre?: readonly [number, number];
};

/** The stage's cylinder with some room: 6 m + half a metre, from the floor to 5 m up. */
export const SHADOW_STAGE = { radius: 6.5, height: 5 } as const satisfies ShadowArea;
export const SHADOW_MAP = 1024;
/** How far up the light stands, along its direction, from the stage's middle. */
const SUN_DISTANCE = 30;

export type ShadowFrame = {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
  readonly near: number;
  readonly far: number;
  /** Metres one shadow-map texel covers, the larger way. */
  readonly texel: number;
};

/**
 * The orthographic frame that holds the stage's cylinder for a light coming
 * from `towardsSun` (unit, from the stage towards the sun), as three.js's
 * shadow camera is set up: at the light, looking at its target, y up.
 */
export function fitShadowCamera(
  towardsSun: THREE.Vector3,
  stage: ShadowArea = SHADOW_STAGE,
  mapSize = SHADOW_MAP,
): ShadowFrame {
  const [ox, oz] = stage.centre ?? [0, 0];
  const centre = new THREE.Vector3(ox, stage.height / 2, oz);
  const eye = centre.clone().addScaledVector(towardsSun, SUN_DISTANCE);
  const z = towardsSun.clone().normalize();
  const up = Math.abs(z.y) > 0.999 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(0, 1, 0);
  const x = new THREE.Vector3().crossVectors(up, z).normalize();
  const y = new THREE.Vector3().crossVectors(z, x);
  let left = Infinity;
  let right = -Infinity;
  let bottom = Infinity;
  let top = -Infinity;
  let near = Infinity;
  let far = -Infinity;
  const v = new THREE.Vector3();
  const samples = 48;
  for (const height of [0, stage.height]) {
    for (let k = 0; k < samples; k++) {
      const a = (2 * Math.PI * k) / samples;
      // A little outside the circle, so the polygon of samples holds all of it.
      const r = stage.radius / Math.cos(Math.PI / samples);
      v.set(ox + r * Math.cos(a), height, oz + r * Math.sin(a)).sub(eye);
      const cx = v.dot(x);
      const cy = v.dot(y);
      const depth = -v.dot(z);
      left = Math.min(left, cx);
      right = Math.max(right, cx);
      bottom = Math.min(bottom, cy);
      top = Math.max(top, cy);
      near = Math.min(near, depth);
      far = Math.max(far, depth);
    }
  }
  return {
    left,
    right,
    top,
    bottom,
    near: Math.max(0.1, near - 1),
    far: far + 1,
    texel: Math.max(right - left, top - bottom) / mapSize,
  };
}

/** A direction from azimuth and elevation, degrees: azimuth from −z, positive towards +x. */
export function directionFrom(azimuth: number, elevation: number): THREE.Vector3 {
  const a = THREE.MathUtils.degToRad(azimuth);
  const e = THREE.MathUtils.degToRad(elevation);
  return new THREE.Vector3(Math.sin(a) * Math.cos(e), Math.sin(e), -Math.cos(a) * Math.cos(e));
}

/** The shadow-casting sun, fitted and ready to add (with its target) to a scene. */
export function makeSun(
  towardsSun: THREE.Vector3,
  colour: THREE.ColorRepresentation,
  intensity: number,
  castShadow = true,
  area: ShadowArea = SHADOW_STAGE,
): THREE.DirectionalLight {
  const light = new THREE.DirectionalLight(colour, intensity);
  light.name = 'sun';
  const [cx, cz] = area.centre ?? [0, 0];
  const centre = new THREE.Vector3(cx, area.height / 2, cz);
  light.position.copy(centre).addScaledVector(towardsSun.clone().normalize(), SUN_DISTANCE);
  light.target.position.copy(centre);
  light.castShadow = castShadow;
  if (castShadow) {
    const frame = fitShadowCamera(towardsSun.clone().normalize(), area);
    const camera = light.shadow.camera;
    camera.left = frame.left;
    camera.right = frame.right;
    camera.top = frame.top;
    camera.bottom = frame.bottom;
    camera.near = frame.near;
    camera.far = frame.far;
    camera.updateProjectionMatrix();
    light.shadow.mapSize.set(SHADOW_MAP, SHADOW_MAP);
    // Soft, and never the acne a sloping floor would otherwise show.
    light.shadow.radius = 3;
    light.shadow.bias = -0.0004;
    light.shadow.normalBias = 0.03;
  }
  return light;
}
